import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function AuthModal({ open, mode, onOpenChange, onSuccessRedirect }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(mode || "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success("Accesso riuscito");
      } else {
        await register(email, password, name);
        toast.success("Account creato");
      }
      onOpenChange(false);
      nav(onSuccessRedirect || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di autenticazione");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-slate-950 text-slate-100 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            <span className="text-gradient">Nexus</span>Cloud
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Accedi o registrati per gestire il tuo storage a vita.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-900">
            <TabsTrigger value="login" data-testid="auth-tab-login">Accedi</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Registrati</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="mt-4 space-y-4">
            <TabsContent value="register" className="m-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Nome</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="border-slate-800 bg-slate-900 text-slate-100"
                  placeholder="Mario Rossi"
                />
              </div>
            </TabsContent>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-slate-800 bg-slate-900 text-slate-100"
                placeholder="tu@esempio.it"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-slate-800 bg-slate-900 text-slate-100"
                placeholder="Almeno 6 caratteri"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300"
            >
              {busy ? "Attendere..." : tab === "login" ? "Accedi" : "Crea account"}
            </Button>

            {tab === "login" && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { onOpenChange(false); nav("/forgot-password"); }}
                  className="text-xs text-slate-400 hover:text-cyan-300"
                >
                  Password dimenticata?
                </button>
              </div>
            )}
          </form>

          <div className="my-4 flex items-center gap-3">
            <Separator className="flex-1 bg-slate-800" />
            <span className="text-xs uppercase tracking-widest text-slate-500">oppure</span>
            <Separator className="flex-1 bg-slate-800" />
          </div>

          <Button
            variant="outline"
            data-testid="auth-google-button"
            onClick={google}
            className="w-full border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
          >
            Continua con Google
          </Button>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
